/**
 * Positron-inspired base style for the map surround.
 * Based on the Positron GL Style (https://github.com/openmaptiles/positron-gl-style)
 * Adapted to work with raw OSM GeoJSON / Overture tiles.
 *
 * This is the DEFAULT surround style. It is intentionally self-contained and
 * free of any other style's concerns (e.g. accessibility / high-contrast):
 * those live in their own style files and reuse the layer skeleton exported
 * here via `createPositronLayers`'s override options.
 *
 * Note: Airport-related features (runways, taxiways, terminals) are excluded
 * as they are rendered separately via dedicated GeoJSON sources.
 */

import type { SurroundColors, SurroundLayer, SurroundStyle } from './surroundStyle.ts';

// Positron palette: soft, low-saturation colors on a light background.
const POSITRON_COLORS: SurroundColors = {
  background: '#f7f7f7',

  // Water - midpoint blue, lightened
  water: '#8acbe4',

  // Parks - more visible green with better saturation
  park: '#c4deab',

  residential: 'rgb(234, 234, 230)',
  wood: 'rgb(210, 218, 210)', // Slightly darker for visibility
  building: 'rgb(234, 234, 229)',
  buildingOutline: 'rgb(219, 219, 218)',

  // Road casing - darker for better contrast
  roadCasing: '#cbcbcb',
  roadCasingMinor: '#cfcfcf', // Lighter casing for residential/minor streets

  // Road colors - increased contrast while maintaining warm tones
  motorway: '#d9d9d9',
  majorRoad: '#e8e8e8',
  cityStreet: '#f5f5f5',
  roadMinor: 'hsl(0, 0%, 85%)', // Darker grey (was 88%)
  roadPath: 'rgb(225, 225, 225)', // Darker (was rgb(234, 234, 234))
  roadSubtle: 'hsla(0, 0%, 82%, 0.75)', // Darker and more opaque

  // Railway colors - slightly more visible
  railway: '#bdbfc7',
  railwayDash: '#f5f5f5',

  // Aviation (Positron `aeroway-*` palette) - tarmac greys for the surface,
  // drawn beneath the service roads so runways/taxiways/aprons read as ground.
  aeroArea: '#eeecec', // apron / tarmac fill (lightest)
  aeroTaxiway: '#cfcfcf', // taxiway / taxilane centreline surface (mid)
  aeroRunway: '#cfcfcf', // runway / stopway surface (matches taxiway)

  text: '#4d4d4d',
  textSubtle: '#4d4d4d',
  textHalo: '#fafafa',

  // Water and park labels are tinted to their feature and drawn without a halo,
  // so the name reads as part of the water or greenery rather than sitting on it.
  waterLabel: '#005476',
  parkLabel: '#00590A',
};

/**
 * Locales whose primary script is non-Latin.
 * Uses underscore-normalized codes matching tile property names.
 */
const NON_LATIN_LOCALES = new Set(['ar', 'ja', 'ko', 'hi', 'zh_Hans', 'zh_Hant', 'th', 'am', 'el', 'ru']);

// Road casing/fill layers cloned below ground for tunnels and filtered above ground to prevent duplicate rendering.
const ROAD_LINE_LAYER_IDS = new Set([
  'positron-highway-minor-casing',
  'positron-highway-minor',
  'positron-highway-major-casing',
  'positron-highway-major-inner',
  'positron-highway-motorway-casing',
  'positron-highway-motorway-inner',
]);

/**
 * Builds a MapLibre text-field expression that selects the locale-specific
 * name property for locality features, falling back to the primary name (v).
 *
 * For non-Latin script locales, returns a bilingual `['format']` expression
 * with the translated name on line 1 and the English name as a smaller
 * subtitle on line 2. The subtitle is suppressed when it would duplicate
 * the primary text.
 *
 * @param locale - BCP-47 locale code (e.g., 'en', 'fr', 'zh-Hans').
 *   If omitted, returns the primary name only.
 */
const localizedTextField = (locale?: string): unknown[] => {
  if (!locale) {
    return ['get', 'v'];
  }
  // Convert BCP-47 subtag separators to underscores to match tile property names
  const prop = `name_${locale.replace(/-/g, '_')}`;
  const translatedExpr = ['coalesce', ['get', prop], ['get', 'v']];

  if (!NON_LATIN_LOCALES.has(prop.replace('name_', ''))) {
    return translatedExpr;
  }

  // Bilingual: translated name + primary name subtitle (suppressed when redundant)
  const englishName = ['get', 'v'];
  const englishSubtitle = [
    'case',
    ['!=', ['to-string', translatedExpr], ['to-string', englishName]],
    ['concat', '\n', englishName],
    '',
  ];

  return ['format', translatedExpr, { 'font-scale': 1.0 }, englishSubtitle, { 'font-scale': 0.85 }];
};

/**
 * Override hooks that let a derived style (e.g. high contrast) reuse this
 * layer skeleton while substituting its own palette, fill treatments, and
 * label halo weight. Every field is optional and defaults to the Positron look.
 */
export type PositronLayerOverrides = {
  /** Palette to color the layers with. Defaults to the Positron palette. */
  colors?: SurroundColors;
  /** Paint object for the water polygon fill (e.g. solid color or fill-pattern). */
  waterPaint?: Record<string, unknown>;
  /** Paint object for the park polygon fill (e.g. solid color or fill-pattern). */
  parkPaint?: Record<string, unknown>;
  /** Halo width for line labels (roads/railways). */
  labelHaloWidth?: number;
};

/**
 * Creates the Positron layer skeleton, optionally with style-specific overrides.
 *
 * Derived styles reuse this so the structural definitions (ids, filters, zoom
 * stops, label placement) live in one place and never drift.
 *
 * @param locale - Active locale for locality label translation.
 * @param overrides - Optional palette / fill / halo overrides.
 */
const createPositronLayers = (locale?: string, overrides: PositronLayerOverrides = {}): SurroundLayer[] => {
  const COLORS = overrides.colors ?? POSITRON_COLORS;
  const waterPaint = overrides.waterPaint ?? {
    'fill-antialias': true,
    'fill-color': COLORS.water,
    // Opaque so the water shade never depends on the backdrop beneath it: with a
    // semi-transparent fill, the sub-pixel gaps between adjacent water polygons blend
    // differently than the body (revealing the near-white backdrop), which reads as a
    // thin dotted seam. An opaque fill + the positron-water-seam line (below) render one
    // uniform shade. (The high-contrast override already uses an opaque water fill.)
    'fill-opacity': 1,
  };
  const parkPaint = overrides.parkPaint ?? { 'fill-antialias': true, 'fill-color': COLORS.park, 'fill-opacity': 0.6 };
  const labelHaloWidth = overrides.labelHaloWidth ?? 1;

  // Smallest park or water body worth naming at each zoom, in hectares from the
  // tile's `a`. Names the big ones early and holds small ones back, which zoom
  // alone cannot do.
  const labelMinHectares = ['step', ['zoom'], 40, 14, 5, 15, 0.5, 16, 0];

  // The same for waterways, in metres of wet course near the venue (`len`). The
  // pannable area is ~13 km across, so 8000 means the water spans most of it —
  // the main river tier.
  const labelMinWaterwayMetres = ['step', ['zoom'], 8000, 14, 4000, 15, 1500, 16, 0];

  // Stand-in for tiles built before waterways carried a length, big enough to
  // clear every tier so those tiles keep their labels.
  const NO_LENGTH_SHOW_ANYWAY = 1e9;

  // Which anchors to show, by the tile's rank `ar`: 0 = only anchor, always
  // shown; 1 = grid anchor, revealed when zoomed in; 2 = centre whose grid takes
  // over up close. Older tiles mark every centre 0 and show all anchors.
  const visibleAnchor = [
    'case',
    ['<', ['zoom'], 13.5],
    ['!=', ['coalesce', ['get', 'ar'], 0], 1],
    ['!=', ['coalesce', ['get', 'ar'], 0], 2],
  ];
  const layers: SurroundLayer[] = [
    // ========================================
    // PARKS AND GREEN SPACES
    // Compact schema: t='p' for park
    // Rendered first (bottom) so water bodies appear on top
    // ========================================
    {
      id: 'positron-park',
      type: 'fill',
      source: 'surroundSource',
      'source-layer': 'surround-features',
      minzoom: 12,
      filter: ['all', ['==', ['geometry-type'], 'Polygon'], ['==', ['get', 't'], 'p']],
      paint: parkPaint,
    },

    // ========================================
    // WATER BODIES (lakes, rivers, etc.)
    // Compact schema: t='w' for water
    // Phase 1 Fix: Support Polygon + MultiPolygon for inland water bodies
    // Rendered after parks so water is visible on top of green/vegetation
    // ========================================
    {
      id: 'positron-water',
      type: 'fill',
      source: 'surroundSource',
      'source-layer': 'surround-features',
      filter: [
        'all',
        ['any', ['==', ['geometry-type'], 'Polygon'], ['==', ['geometry-type'], 'MultiPolygon']],
        ['==', ['get', 't'], 'w'],
      ],
      paint: waterPaint,
    },

    // ========================================
    // WATER SEAM COVER
    // Water arrives as many separate polygon features; adjacent ones don't always share
    // exact vertices, so their shared edge leaves a sub-pixel gap that reveals the
    // near-white backdrop as a thin dotted seam over open water (most visible when
    // pitched into 3D, and only at zooms where the polygons don't tile exactly).
    // Painting the polygon boundaries in the water color bridges those gaps. Lives in the
    // shared skeleton (line-color from COLORS.water) so it covers both the default and
    // high-contrast palettes. The real fix is to dissolve adjacent water polygons in the
    // surround tile pipeline.
    // ========================================
    {
      id: 'positron-water-seam',
      type: 'line',
      source: 'surroundSource',
      'source-layer': 'surround-features',
      filter: [
        'all',
        ['any', ['==', ['geometry-type'], 'Polygon'], ['==', ['geometry-type'], 'MultiPolygon']],
        ['==', ['get', 't'], 'w'],
      ],
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': COLORS.water,
        'line-width': 3,
      },
    },

    // ========================================
    // WATER COASTLINES (bay/ocean boundaries, major rivers)
    // Compact schema: t='w' for water
    // Phase 1 Fix: Render LineString water features (SF Bay coastline = 1,658 features)
    // Note: This renders coastlines as blue strokes on gray background (not cartographically
    // perfect, but makes water visible). Phase 2 will implement proper inverted water model
    // with land polygons and blue background.
    // ========================================
    // {
    //   id: 'positron-water-coastline',
    //   type: 'line',
    //   source: 'surroundSource',
    //   'source-layer': 'surround-features',
    //   filter: ['all', ['==', ['geometry-type'], 'LineString'], ['==', ['get', 't'], 'w']],
    //   paint: {
    //     'line-color': COLORS.water,
    //     'line-width': ['interpolate', ['linear'], ['zoom'], 12, 3, 14, 5, 16, 8, 18, 12, 20, 16],
    //     'line-opacity': 0.8,
    //   },
    // },

    // ========================================
    // RAILWAYS - THIN TRACK LINE
    // Compact schema: t='y' for railway
    // ========================================
    {
      id: 'positron-railway',
      type: 'line',
      source: 'surroundSource',
      'source-layer': 'surround-features',
      minzoom: 12,
      filter: ['all', ['==', ['geometry-type'], 'LineString'], ['==', ['get', 't'], 'y']],
      layout: {
        'line-join': 'round',
        'line-cap': 'butt',
      },
      paint: {
        'line-color': COLORS.railway,
        'line-width': {
          base: 1.3,
          stops: [
            [12, 0.75],
            [20, 1.5],
          ],
        },
      },
    },

    // ========================================
    // RAILWAYS - CROSS-HATCH TICK MARKS (sleepers) - zoomed out (wider spacing)
    // ========================================
    {
      id: 'positron-railway-ticks-far',
      type: 'line',
      source: 'surroundSource',
      'source-layer': 'surround-features',
      minzoom: 12,
      maxzoom: 14,
      filter: ['all', ['==', ['geometry-type'], 'LineString'], ['==', ['get', 't'], 'y']],
      layout: {
        'line-join': 'round',
        'line-cap': 'butt',
      },
      paint: {
        'line-color': COLORS.railway,
        'line-dasharray': [0.1, 3.5],
        'line-width': {
          base: 1.3,
          stops: [
            [12, 2.5],
            [14, 4],
          ],
        },
      },
    },

    // ========================================
    // RAILWAYS - CROSS-HATCH TICK MARKS (sleepers) - zoomed in (tighter spacing)
    // ========================================
    {
      id: 'positron-railway-ticks-near',
      type: 'line',
      source: 'surroundSource',
      'source-layer': 'surround-features',
      minzoom: 14,
      filter: ['all', ['==', ['geometry-type'], 'LineString'], ['==', ['get', 't'], 'y']],
      layout: {
        'line-join': 'round',
        'line-cap': 'butt',
      },
      paint: {
        'line-color': COLORS.railway,
        'line-dasharray': [0.1, 1.8],
        'line-width': {
          base: 1.3,
          stops: [
            [14, 4],
            [20, 8],
          ],
        },
      },
    },

    // ========================================
    // AVIATION SURFACES (runways, taxiways, taxilanes, stopways, aprons)
    // Compact schema: t='a', s contains the Overture class. No width attribute;
    // runways are styled purely via zoom-interpolated line width.
    //
    // Sourced from Overture base/infrastructure by map-surround; these replace the
    // venue's hand-drawn runway/apron art (hidden via conflictingLayers). They are
    // inserted BEFORE the minor-road layers below so the tarmac reads as a surface
    // *beneath* the service roads. Intra-aviation draw order is apron (bottom) →
    // taxiway → service roads → runway (top): later layers paint on top.
    // ========================================

    // AVIATION - APRON / POLYGON SURFACE FILL (tarmac surface)
    // Aprons are the only aviation feature Overture encodes as a polygon;
    // runways/stopways/taxiways/taxilanes arrive as LineStrings and are drawn by
    // the line layers below.
    {
      id: 'positron-aeroway-area',
      type: 'fill',
      source: 'surroundSource',
      'source-layer': 'surround-features',
      minzoom: 12,
      filter: [
        'all',
        ['any', ['==', ['geometry-type'], 'Polygon'], ['==', ['geometry-type'], 'MultiPolygon']],
        ['==', ['get', 't'], 'a'],
        ['==', ['get', 's'], 'apron'],
      ],
      paint: {
        'fill-antialias': true,
        'fill-color': COLORS.aeroArea,
        'fill-opacity': 1,
      },
    },

    // AVIATION - TAXIWAY / TAXILANE LINE
    // No width attribute in data; relies purely on zoom-interpolated line width.
    {
      id: 'positron-aeroway-taxiway',
      type: 'line',
      source: 'surroundSource',
      'source-layer': 'surround-features',
      minzoom: 12,
      filter: [
        'all',
        ['==', ['geometry-type'], 'LineString'],
        ['==', ['get', 't'], 'a'],
        ['any', ['==', ['get', 's'], 'taxiway'], ['==', ['get', 's'], 'taxilane']],
      ],
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': COLORS.aeroTaxiway,
        // Exponential-base zoom interpolation for smooth, zoom-stable width.
        'line-width': ['interpolate', ['exponential', 1.5], ['zoom'], 12, 1, 14, 3, 16, 7, 18, 14, 20, 24],
      },
    },

    // ========================================
    // Overture road classes are: motorway primary secondary tertiary residential living_street trunk unclassified service
    // Compact schema: t='r' for roads, s contains the class

    // ========================================
    // HIGHWAYS - MINOR ROADS CASING (outline) unclassified, residential, living_street, service
    // ========================================
    {
      id: 'positron-highway-minor-casing',
      type: 'line',
      source: 'surroundSource',
      'source-layer': 'surround-features',
      minzoom: 13,
      filter: [
        'all',
        ['==', ['geometry-type'], 'LineString'],
        ['==', ['get', 't'], 'r'],
        [
          'any',
          ['==', ['get', 's'], 'unclassified'],
          ['==', ['get', 's'], 'residential'],
          ['==', ['get', 's'], 'living_street'],
          ['==', ['get', 's'], 'service'], // roads on the tarmac; drawn on top of the surround aviation surface
        ],
      ],
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': COLORS.roadCasingMinor,
        'line-opacity': ['interpolate', ['linear'], ['zoom'], 13.5, 0, 14.5, 1],
        'line-dasharray': [12, 0],
        'line-width': {
          base: 1.4,
          stops: [
            [10, 3],
            [20, 23],
          ],
        },
      },
    },

    // ========================================
    // HIGHWAYS - MINOR ROADS FILL (residential, unclassified, residential, living_street, service)
    // ========================================
    {
      id: 'positron-highway-minor',
      type: 'line',
      source: 'surroundSource',
      'source-layer': 'surround-features',
      minzoom: 13,
      filter: [
        'all',
        ['==', ['geometry-type'], 'LineString'],
        ['==', ['get', 't'], 'r'],
        [
          'any',
          ['==', ['get', 's'], 'unclassified'], // seems to be thing lanes closest to the venue
          ['==', ['get', 's'], 'residential'],
          ['==', ['get', 's'], 'living_street'],
          ['==', ['get', 's'], 'service'], // these are the roads on the tarmac; the surround aviation surface sits beneath them
        ],
      ],
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': COLORS.cityStreet,
        'line-opacity': ['interpolate', ['linear'], ['zoom'], 13.5, 0, 14.5, 1],
        'line-width': {
          base: 1.4,
          stops: [
            [10, 2],
            [20, 20],
          ],
        },
      },
    },

    // AVIATION - RUNWAY / STOPWAY SURFACE (no casing; drawn on top of service roads)
    {
      id: 'positron-aeroway-runway',
      type: 'line',
      source: 'surroundSource',
      'source-layer': 'surround-features',
      minzoom: 12,
      filter: [
        'all',
        ['==', ['geometry-type'], 'LineString'],
        ['==', ['get', 't'], 'a'],
        ['any', ['==', ['get', 's'], 'runway'], ['==', ['get', 's'], 'stopway']],
      ],
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': COLORS.aeroRunway,
        // Exponential-base zoom interpolation matching taxiways, but scaled up to be wider.
        'line-width': ['interpolate', ['exponential', 1.5], ['zoom'], 12, 3, 14, 9, 16, 20, 18, 40, 20, 72],
      },
    },

    // ========================================
    // HIGHWAYS - MAJOR ROADS CASING (outline) primary, secondary, tertiary, trunk
    // ========================================
    {
      id: 'positron-highway-major-casing',
      type: 'line',
      source: 'surroundSource',
      'source-layer': 'surround-features',
      minzoom: 11,
      filter: [
        'all',
        ['==', ['geometry-type'], 'LineString'],
        ['==', ['get', 't'], 'r'],
        [
          'any',
          ['==', ['get', 's'], 'primary'],
          ['==', ['get', 's'], 'secondary'],
          ['==', ['get', 's'], 'tertiary'],
          ['==', ['get', 's'], 'trunk'],
        ],
      ],
      layout: {
        'line-cap': 'butt',
        'line-join': 'round',
      },
      paint: {
        'line-color': COLORS.roadCasing,
        'line-dasharray': [12, 0],
        'line-width': {
          base: 1.3,
          stops: [
            [10, 3],
            [20, 23],
          ],
        },
      },
    },

    // ========================================
    // HIGHWAYS - MAJOR ROADS INNER (fill)
    // ========================================
    {
      id: 'positron-highway-major-inner',
      type: 'line',
      source: 'surroundSource',
      'source-layer': 'surround-features',
      minzoom: 11,
      filter: [
        'all',
        ['==', ['geometry-type'], 'LineString'],
        ['==', ['get', 't'], 'r'],
        [
          'any',
          ['==', ['get', 's'], 'primary'],
          ['==', ['get', 's'], 'secondary'],
          ['==', ['get', 's'], 'tertiary'],
          ['==', ['get', 's'], 'trunk'],
        ],
      ],
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': COLORS.majorRoad,
        'line-width': {
          base: 1.3,
          stops: [
            [10, 2],
            [20, 20],
          ],
        },
      },
    },

    // ========================================
    // HIGHWAYS - MOTORWAY CASING (outline)
    // ========================================
    {
      id: 'positron-highway-motorway-casing',
      type: 'line',
      source: 'surroundSource',
      'source-layer': 'surround-features',
      minzoom: 11,
      filter: [
        'all',
        ['==', ['geometry-type'], 'LineString'],
        ['==', ['get', 't'], 'r'],
        ['any', ['==', ['get', 's'], 'motorway']],
      ],
      layout: {
        'line-cap': 'butt',
        'line-join': 'round',
      },
      paint: {
        'line-color': COLORS.roadCasing,
        'line-dasharray': [2, 0],
        'line-opacity': 1,
        'line-width': {
          base: 1.4,
          stops: [
            [5.8, 0],
            [6, 3.5],
            [20, 40],
          ],
        },
      },
    },

    // ========================================
    // HIGHWAYS - MOTORWAY INNER (fill)
    // ========================================
    {
      id: 'positron-highway-motorway-inner',
      type: 'line',
      source: 'surroundSource',
      'source-layer': 'surround-features',
      minzoom: 11,
      filter: [
        'all',
        ['==', ['geometry-type'], 'LineString'],
        ['==', ['get', 't'], 'r'],
        ['any', ['==', ['get', 's'], 'motorway']],
      ],
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': {
          base: 1,
          stops: [
            [5.8, 'hsla(0, 0%, 85%, 0.53)'],
            [6, COLORS.motorway],
          ],
        },
        'line-width': {
          base: 1.4,
          stops: [
            [4, 2],
            [6, 1.3],
            [20, 30],
          ],
        },
      },
    },

    // ========================================
    // LABELS - HIGHWAY NAMES
    // Compact schema: t='r', s='motorway', v contains the name
    // ========================================
    {
      id: 'positron-highway-name-other',
      type: 'symbol',
      source: 'surroundSource',
      'source-layer': 'surround-features',
      minzoom: 15,
      filter: [
        'all',
        ['==', ['geometry-type'], 'LineString'],
        ['==', ['get', 't'], 'r'],
        ['!', ['has', 'u']],
        ['==', ['get', 's'], 'motorway'],
        ['!=', ['get', 'v'], ''], // has a name
      ],
      layout: {
        'symbol-placement': 'line',
        'symbol-spacing': 350,
        'text-field': ['get', 'v'],
        'text-font': ['Noto Sans Bold'],
        'text-max-angle': 30,
        'text-pitch-alignment': 'viewport',
        'text-rotation-alignment': 'map',
        'text-size': 9,
        'text-transform': 'uppercase',
      },
      paint: {
        'text-color': COLORS.textSubtle,
        'text-halo-blur': 0,
        'text-halo-color': '#ffffff',
        'text-halo-width': labelHaloWidth,
      },
    },
    // ========================================
    // LABELS - RAILWAY NAMES
    // Compact schema: t='y', v contains the name
    // ========================================
    {
      id: 'positron-railway-name',
      type: 'symbol',
      source: 'surroundSource',
      'source-layer': 'surround-features',
      minzoom: 15,
      filter: [
        'all',
        ['==', ['geometry-type'], 'LineString'],
        ['==', ['get', 't'], 'y'],
        ['!=', ['get', 'v'], ''], // has a name
      ],
      layout: {
        'symbol-placement': 'line',
        'symbol-spacing': 450,
        'text-field': ['get', 'v'],
        'text-font': ['Noto Sans Bold'],
        'text-max-angle': 30,
        'text-pitch-alignment': 'viewport',
        'text-rotation-alignment': 'map',
        'text-size': 10,
        'text-transform': 'uppercase',
      },
      paint: {
        'text-color': COLORS.textSubtle,
        'text-halo-blur': 0,
        'text-halo-color': '#ffffff',
        'text-halo-width': labelHaloWidth,
      },
    },
    // ========================================
    // PARK LABELS — before locality labels, so city names win collisions
    // ========================================
    {
      id: 'positron-park-labels',
      type: 'symbol',
      source: 'surroundSource',
      'source-layer': 'surround-features',
      // Parks are dense in urban surrounds (playgrounds, dog parks, rec centers all
      // carry names) — hold labels until well zoomed in to avoid label soup.
      minzoom: 14.5,
      filter: [
        'all',
        // Anchor points, not the polygon: a park spanning several tiles would
        // otherwise be labeled once per tile.
        ['==', ['geometry-type'], 'Point'],
        ['==', ['get', 't'], 'p'],
        ['!=', ['get', 'v'], ''], // has a name
        ['>=', ['coalesce', ['get', 'a'], 0], labelMinHectares],
        visibleAnchor,
      ],
      layout: {
        'text-field': localizedTextField(locale),
        'text-font': ['Noto Sans Bold'],
        'text-size': 12,
        'text-max-width': 8,
        'text-letter-spacing': 0.05,
      },
      paint: {
        'text-color': COLORS.parkLabel,
      },
    },
    // ========================================
    // WATER LABELS - AREAS (lakes, oceans, bays) — one label per anchor point
    // ========================================
    {
      id: 'positron-water-labels-point',
      type: 'symbol',
      source: 'surroundSource',
      'source-layer': 'surround-features',
      // Past the landing view (the surround's minimum zoom is 12), so big water
      // does not compete with the terminal POIs the moment the map loads.
      minzoom: 13.5,
      filter: [
        'all',
        ['==', ['geometry-type'], 'Point'],
        ['==', ['get', 't'], 'w'],
        ['!=', ['get', 'v'], ''], // has a name
        // A basin is worth naming from far out, a fountain only up close.
        ['>=', ['coalesce', ['get', 'a'], 0], labelMinHectares],
        visibleAnchor,
      ],
      layout: {
        'text-field': localizedTextField(locale),
        'text-font': ['Noto Sans Bold'],
        'text-size': 12,
        // Open water has room, so let these names run on one line
        'text-max-width': 14,
        // Same tracking as park labels; wide spacing looks stretched on a pond.
        'text-letter-spacing': 0.05,
      },
      paint: {
        'text-color': COLORS.waterLabel,
      },
    },
    // ========================================
    // WATER LABELS - LINES (rivers, canals) — name follows the water's course
    // ========================================
    {
      id: 'positron-water-labels-line',
      type: 'symbol',
      source: 'surroundSource',
      'source-layer': 'surround-features',
      // Same threshold as the water point layer. They used to differ by half a
      // zoom, which put small channels on screen before the Tidal Basin.
      minzoom: 13.5,
      filter: [
        'all',
        ['==', ['geometry-type'], 'LineString'],
        ['==', ['get', 't'], 'w'],
        ['!=', ['get', 'v'], ''], // has a name
        // Name the big water first, by length in `len`. It is a separate property
        // from `a` so a length is never compared against an area. Tiles without
        // `len` fall back to showing the label.
        ['>=', ['coalesce', ['get', 'len'], NO_LENGTH_SHOW_ANYWAY], labelMinWaterwayMetres],
      ],
      layout: {
        'symbol-placement': 'line',
        // Roughly a viewport wide, so a river reads as named once or twice as you
        // pan along it rather than repeating every few hundred pixels.
        'symbol-spacing': 1000,
        'text-field': localizedTextField(locale),
        'text-font': ['Noto Sans Bold'],
        'text-max-angle': 30,
        'text-pitch-alignment': 'viewport',
        'text-rotation-alignment': 'map',
        'text-size': 12,
        // Same tracking as the other surround labels.
        'text-letter-spacing': 0.05,
      },
      paint: {
        'text-color': COLORS.waterLabel,
      },
    },
    // ========================================
    // LOCALITY LABELS (CITIES, TOWNS, VILLAGES)
    // Compact schema: t='l', s contains the class, v contains the name
    // ========================================
    {
      id: 'positron-locality-labels',
      type: 'symbol',
      source: 'surroundSource',
      'source-layer': 'surround-features',
      minzoom: 10,
      filter: [
        'all',
        ['==', ['geometry-type'], 'Point'],
        ['==', ['get', 't'], 'l'],
        ['!=', ['get', 'v'], ''], // has a name
      ],
      layout: {
        'text-field': localizedTextField(locale),
        'text-font': ['Noto Sans Bold'],
        'text-size': [
          'match',
          ['get', 's'],
          'city',
          16,
          'town',
          14,
          'village',
          12,
          'hamlet',
          10,
          'neighbourhood',
          10,
          12, // default
        ],
        'text-transform': 'uppercase',
        'text-letter-spacing': 0.05,
        'text-max-width': 8,
      },
      paint: {
        'text-color': COLORS.text,
        'text-halo-color': COLORS.textHalo,
        'text-halo-width': 2,
        'text-halo-blur': 1,
      },
    },
  ];

  const tunnelLayers = layers
    .filter(layer => ROAD_LINE_LAYER_IDS.has(layer.id))
    .map(layer => ({
      ...layer,
      id: layer.id.replace('positron-highway-', 'positron-highway-tunnel-'),
      filter: [...(Array.isArray(layer.filter) ? layer.filter : ['all']), ['==', ['get', 'u'], 1]],
    }));
  const aboveGroundLayers = layers.map(layer =>
    ROAD_LINE_LAYER_IDS.has(layer.id)
      ? { ...layer, filter: [...(Array.isArray(layer.filter) ? layer.filter : ['all']), ['!', ['has', 'u']]] }
      : layer,
  );

  // Tunnels render beneath all surface features(parks, water, roads, labels),
  // so their cloned layers are prepended rather than interleaved with above-ground roads.
  return [...tunnelLayers, ...aboveGroundLayers];
};

// Transitions removed to improve performance at high zoom levels
// Previously had 5-second fade-in transitions that caused freezing at zoom 21

/** The default Positron surround style. */
const positronStyle: SurroundStyle = {
  id: 'positron',
  colors: POSITRON_COLORS,
  createLayers: locale => createPositronLayers(locale),
};

export { localizedTextField, NON_LATIN_LOCALES, POSITRON_COLORS, createPositronLayers };
export default positronStyle;
